import React, { useMemo } from "react";
import styled from "styled-components";
import { Button, Icon, NonIdealState, Spinner } from "@blueprintjs/core";

import { SingleMatchPanel, SingleResultPanel } from "@components";
import { useRunResponse, useRunTestResults } from "@hooks";
import { CliMatch } from "@types";
import {
  CONFIG_DOC_LINK,
  makeDockerLink,
  makeDockerShortLink,
  makeResultKey,
  PATTERN_DOC_LINK,
} from "@utils";

import { RunResponse } from "@pages/editor/types";

interface Props {
  runResponse: RunResponse;
  pattern: string;
  dirty: boolean;
  loading: boolean;
  hasRun: boolean;
  onRun: () => void;
  reportBug: () => void;
  onFindingClick: (line?: number) => () => void;
  onAutoTests?: () => void;
  onApplyAllFixes?: () => void;
  onApplyFix?: (r: CliMatch) => () => void;
  fixesApplied?: { [key: string]: boolean };
}

const ErrorBox = styled.div`
  white-space: pre-wrap;
`;

const runTimeInfo = (imageId: string | undefined, runTime: number) => {
  return (
    imageId !== undefined && (
      <>
        Run with version:{" "}
        <code title={imageId}>
          <a
            href={makeDockerLink(imageId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {makeDockerShortLink(imageId)}
          </a>
        </code>{" "}
        in {Math.round(runTime * 100) / 100.0}s
      </>
    )
  );
};

const invalidPatternHelp = (
  <div className="quick-help invalid-pattern">
    The{" "}
    <a href={PATTERN_DOC_LINK} target="_blank" rel="noopener noreferrer">
      pattern documentation
    </a>{" "}
    has full details on pattern syntax. Here are some common issues:
    <ul className="bp3-list">
      <li>Source code must be syntactically valid.</li>
      <li>
        Fragments of code are not valid patterns.{" "}
        <code className="bp3-code">1+</code> or{" "}
        <code className="bp3-code">if $X:</code> are not valid patterns because
        they are not full AST elements.
      </li>
      <li>
        Metavariable (<code className="bp3-code">$X</code>,{" "}
        <code className="bp3-code">$Y</code>,{" "}
        <code className="bp3-code">$WHATEVER</code>) names must be UPPERCASE
        letters (A-Z). <code className="bp3-code">$x</code> and{" "}
        <code className="bp3-code">$some_var</code> are not valid metavariable
        names.
      </li>
    </ul>
  </div>
);

const noMatchesHelp = (
  <div className="quick-help">
    Try tweaking your pattern or code or open a bug if you feel there should be
    a match.
    <br />
    <br />
    The{" "}
    <a href={CONFIG_DOC_LINK} target="_blank" rel="noopener noreferrer">
      config documentation
    </a>{" "}
    has more details on the syntax and boolean logic. Here are some common
    issues:
    <ul className="bp3-list">
      <li>
        Module names must be fully specified: to match{" "}
        <pre className="bp3-code">
          {"import google.metrics\nmetrics.send(foo)"}
        </pre>
        your pattern needs to be{" "}
        <code className="bp3-code">google.metrics.send(...)</code>.<br />
        <code className="bp3-code">metrics.send(...)</code> will not match.
      </li>
      <li>
        Metavariable names can only contain uppercase characters,{" "}
        <code className="bp3-code">_</code>, or digits, and must start with an
        uppercase character or <code className="bp3-code">_</code>. Names like{" "}
        <code className="bp3-code">$x</code> or{" "}
        <code className="bp3-code">$some_value</code> are invalid.
      </li>
      <li>
        Some boolean combinations of patterns will never match anything. For
        instance:
        <pre className="bp3-code">
          {"patterns:\n    pattern: foo(1)\n    pattern: foo(2)"}
        </pre>
        will never match any code, because foo cannot have both 1 and 2 as its
        first argument.
      </li>
    </ul>
  </div>
);

export const Results: React.FC<Props> = ({
  runResponse,
  dirty,
  hasRun,
  loading,
  reportBug,
  onAutoTests,
  onApplyAllFixes,
  onApplyFix,
  onFindingClick,
  fixesApplied,
}) => {
  const {
    findings,
    allErrors,
    patternErrors,
    targetErrors,
    fatalErrors,
    runError,
  } = useRunResponse(runResponse);
  const { numTestFailures } = useRunTestResults(runResponse);

  const noMatches = findings.length === 0 && allErrors.length === 0;

  const reportBugButton = (
    <div className="error-cta">
      <span className="align-button-text">
        If you expected matches for this pattern, please
      </span>
      <Button
        onClick={reportBug}
        icon="issue-new"
        intent="danger"
        minimal={true}
      >
        file a bug
      </Button>{" "}
      You can also chat with{" "}
      <a href="https://r2c.dev/slack">the Semgrep developers on Slack!</a> We're
      friendly and would be happy to help you.
    </div>
  );

  const spanErrors = useMemo(
    () => [...patternErrors, ...targetErrors],
    [patternErrors, targetErrors]
  );
  const internalErrors = useMemo(
    () => [...fatalErrors, ...(runError ? [runError] : [])],
    [runError, fatalErrors]
  );

  return (
    <div>
      <div className={`results-container${findings && dirty ? " dirty" : ""}`}>
        {onApplyAllFixes &&
          findings.filter((r) => r.extra.fixed_lines).length > 0 &&
          fixesApplied !== undefined && (
            <Button
              className="apply-all-fix-btn"
              small
              onClick={onApplyAllFixes}
              disabled={
                dirty ||
                findings.filter(
                  (r) => r.extra.fixed_lines && !fixesApplied[makeResultKey(r)]
                ).length === 0
              }
            >
              Apply all fixes
            </Button>
          )}
        {internalErrors.length > 0 && (
          <>
            <h3 className="title">FATAL ERROR</h3>
            <div>
              <pre className="bp3-code-block">
                {internalErrors.map((error, idx) => (
                  <ErrorBox key={idx}>
                    {typeof error === "string"
                      ? error
                      : JSON.stringify(error).includes("MatchTimeoutError")
                      ? "Semgrep Timed Out"
                      : JSON.stringify(error).replace(/(^")|("$)/g, "")}
                  </ErrorBox>
                ))}
              </pre>
              {invalidPatternHelp}
            </div>
          </>
        )}
        {!hasRun ? (
          <div>
            <NonIdealState
              className="non-results"
              title=""
              description="Run Semgrep to see results for your pattern"
              action={undefined}
            />
          </div>
        ) : noMatches ? (
          <NonIdealState
            className="non-results"
            icon="zoom-out"
            title="No Matches"
            description={noMatchesHelp}
            action={reportBugButton}
          />
        ) : (
          <>
            {loading ? (
              <Spinner className="results results-spin" />
            ) : (
              <>
                {internalErrors.length === 0 && spanErrors.length > 0 && (
                  <>
                    <h3 className="title">ERRORS</h3>
                    <div className="results">
                      {spanErrors.map((error, idx) => (
                        <SingleResultPanel
                          key={`err-${idx}`}
                          onResultClick={onFindingClick}
                          line={error.spans?.[0]?.start?.line}
                          message={error.message}
                        />
                      ))}
                    </div>
                  </>
                )}
                {internalErrors.length === 0 && findings.length > 0 && (
                  <>
                    <h3 className="title">MATCHES</h3>
                    <div className="results">
                      {findings.map((r, idx) => (
                        <SingleMatchPanel
                          key={`res-${idx}`}
                          onResultClick={onFindingClick}
                          item={r}
                          line={r.start.line}
                          message={r.extra.message}
                          dirty={dirty}
                          fixesApplied={fixesApplied}
                          fixes={r.extra.fixed_lines}
                          onApplyFix={onApplyFix}
                        />
                      ))}
                    </div>
                  </>
                )}
                {numTestFailures >= 0 && (
                  <div className="test-results">
                    {numTestFailures === 0 ? (
                      <div className="all-tests-pass">
                        <Icon
                          className="icon-margin-after"
                          size={Icon.SIZE_STANDARD}
                          icon="tick"
                        />
                        All tests pass
                      </div>
                    ) : (
                      <div className="test-failures">
                        <Icon
                          className="icon-margin-after"
                          size={Icon.SIZE_STANDARD}
                          icon="warning-sign"
                        />
                        {numTestFailures}{" "}
                        {numTestFailures === 1 ? "test" : "tests"} failed
                      </div>
                    )}
                    {onAutoTests && numTestFailures !== 0 && (
                      <div className="auto-tests">
                        <Button
                          icon="automatic-updates"
                          onClick={onAutoTests}
                          disabled={dirty}
                          outlined
                        >
                          Update Test Comments
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                {allErrors.length > 0 && (
                  <div className="parse-error-report-bug">
                    {reportBugButton}
                  </div>
                )}
              </>
            )}
          </>
        )}
        <div className="run-time-info bp3-running-text bp3-text-muted">
          {runResponse.semgrep_result.run_time !== undefined &&
            runTimeInfo(
              runResponse.semgrep_result.output.version,
              runResponse.semgrep_result.run_time
            )}{" "}
        </div>
      </div>
      {runResponse.semgrep_result.run_time ? <hr /> : undefined}
    </div>
  );
};
